/**
 * The one-line summary an agent writes for a tool call, as row copy.
 *
 * A transcript row normally ignores the title: our verb tables say what the
 * call did ("Read", "Searched for") and its arguments say what it acted on,
 * which reads consistently across agents and gives a path the preview
 * affordance a raw sentence can't have. That only works while the arguments
 * arrive. Cursor publishes a call the moment it *starts* streaming them and
 * never re-sends it once they complete, so a call can reach us with
 * `rawInput: {}`, no `locations`, and its title as the only record of what it
 * acted on — and our verb then replaces the one thing naming the file.
 *
 * So a row with no subject of its own falls back to the title
 * ({@link ../turn-blocks/ToolCallBlockView.tsx} decides which rows those are).
 * The title has to earn that: most agents title a call with the *name of the
 * tool* (`Read`, `BashOutput`, `read_file`), which records nothing the row
 * doesn't already say and would cost it the tensed verb it has words for. A
 * name is one token; a record of a subject is a sentence. That is the whole
 * test, and it is a shape rather than a list because the tools are open-ended:
 * every agent may add one tomorrow.
 */

import { truncate } from '@reflex/ui/lib/format';
import { UNTITLED_TOOL_CALL } from './parsers.js';

/**
 * A ceiling on the label's length. The row clips it in CSS and keeps the full
 * text on hover, so this is not what makes it fit — it bounds what a streamed
 * title can put in the DOM.
 */
const MAX_TITLE_LENGTH = 80;

/**
 * A tool-call title as row copy, or `null` when it says nothing the row's own
 * words don't already.
 *
 * Agents quote the operands inside a title (Cursor writes ``Edit `src/app.ts` ``
 * and wraps a shell command in backticks), which is markup for a renderer that
 * never runs on this string — the row is plain text — so the backticks come
 * off. Newlines do too: the row is one line, and a multi-line command would
 * otherwise blow out its height.
 */
export function toolCallTitleLabel(title: string): string | null {
  const cleaned = title.replaceAll('`', '').replace(/\s+/g, ' ').trim();
  if (!cleaned || cleaned === UNTITLED_TOOL_CALL) return null;
  // One token is a tool's name, not an account of what it did.
  if (!cleaned.includes(' ')) return null;
  return truncate(cleaned, MAX_TITLE_LENGTH);
}
